// src/pages/motolivraison/table.helpers.jsx
//
// Éléments partagés par tous les tableaux du module MotoLivraison, pour que les
// pages Commerces / Commerçants / Livreurs / Commandes / Reversements aient
// exactement la même présentation :
//   - en-tête ardoise (classe CSS `moto-table`, voir css/motolivraison-table.css)
//   - libellés de tri et de filtre en français
//   - pagination 10 par défaut avec sélecteur de taille
//   - boutons d'action à fond plein AVEC libellé

import React from 'react';
import { Card, Space, Tooltip } from 'antd';

// Libellés français des tableaux (AntD est en anglais par défaut sur ce projet)
export const LOCALE_TABLE = {
  triggerDesc: 'Trier par ordre décroissant',
  triggerAsc: 'Trier par ordre croissant',
  cancelSort: 'Annuler le tri',
  filterTitle: 'Filtrer',
  filterConfirm: 'Appliquer',
  filterReset: 'Réinitialiser',
  filterSearchPlaceholder: 'Rechercher',
  selectAll: 'Tout sélectionner',
};

// Pagination standard : `libelle` est le nom de l'entité au singulier.
export const PAGINATION_TABLE = (libelle) => ({
  defaultPageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50', '100'],
  showTotal: (total, [debut, fin]) =>
    `${debut}-${fin} sur ${total} ${libelle}${total > 1 ? 's' : ''}`,
});

// Boutons d'action : fond de couleur pleine + libellé explicite.
// On n'utilise PAS type="primary" : une règle globale (!important) repeint tous
// les boutons primaires en dégradé doré, les actions seraient indistinctes.
export const BTN_ACTION = (couleur) => ({
  background: couleur,
  borderColor: couleur,
  color: '#fff',
});

// Palette des actions, pour rester cohérent d'une page à l'autre
export const COULEURS_ACTION = {
  voir: '#1677ff',      // consulter / détails
  modifier: '#1677ff',
  valider: '#52c41a',   // approuver / réactiver / payer
  attention: '#fa8c16', // désactiver / suspendre temporairement
  danger: '#f5222d',    // supprimer / rejeter / annuler
};

// Comparateur texte tolérant aux valeurs vides (tri alphabétique français)
export const cmpTexte = (a, b) => String(a || '').localeCompare(String(b || ''), 'fr');

// Carte de statistique compacte (les grandes cartes AntD mangeaient l'écran)
export const CarteStat = ({ label, value, icon, color = '#595959' }) => (
  <Card size="small" styles={{ body: { padding: '10px 14px' } }}>
    <Space align="center" size={10}>
      <span style={{ fontSize: 18, color, display: 'flex' }}>{icon}</span>
      <div style={{ lineHeight: 1.2 }}>
        <div style={{ fontSize: 12, color: '#8c8c8c' }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 600, color }}>{value}</div>
      </div>
    </Space>
  </Card>
);

// Tooltip de cellule : un intitulé discret + la valeur complète, lisible et sobre.
// À utiliser avec `ellipsis: { showTitle: false }` sur la colonne, pour remplacer
// l'info-bulle brute du navigateur.
export const CellTooltip = ({ label, value, extra, children }) => {
  if (!value) return children;
  return (
    <Tooltip
      placement="topLeft"
      mouseEnterDelay={0.35}
      color="#1f1f1f"
      title={
        <div style={{ maxWidth: 280, lineHeight: 1.45 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.65 }}>
            {label}
          </div>
          <div style={{ fontSize: 13 }}>{value}</div>
          {extra && <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{extra}</div>}
        </div>
      }
    >
      {children}
    </Tooltip>
  );
};
